import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchQueuePause, PAUSABLE_JOBS } from '../api/queue';
import { useAuth } from '../context/AuthContext';

// Best-effort warning strip shown to every role when any NS-writing queue is
// paused, so nobody wonders why approved work isn't reaching NetSuite. Polls lightly.
const POLL_MS = 30_000;

export default function QueuePausedBanner() {
  const { user } = useAuth();
  const [pausedLabels, setPausedLabels] = useState<string[]>([]);

  useEffect(() => {
    let active = true;

    async function check() {
      try {
        // A timer, not the user: don't extend the session (decision pollsession).
        const rows = await fetchQueuePause({ background: true });
        const labels = PAUSABLE_JOBS.filter(meta =>
          rows.some(r => r.job_type === meta.jobType && r.is_paused),
        ).map(meta => meta.bannerLabel);
        if (active) setPausedLabels(labels);
      } catch {
        // silent — the banner must never break the app shell
      }
    }

    void check();
    const id = setInterval(() => { void check(); }, POLL_MS);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (pausedLabels.length === 0) return null;

  const what = pausedLabels.join(' and ');
  const verb = pausedLabels.length > 1 ? 'are' : pausedLabels[0].endsWith('s') ? 'are' : 'is';

  return (
    <div className="flex shrink-0 items-center justify-center gap-2 bg-amber-100 px-4 py-1.5 text-center text-xs font-medium text-amber-800">
      <span>
        ⏸ {what.charAt(0).toUpperCase() + what.slice(1)} to NetSuite {verb} paused — affected jobs
        stay queued until resumed.
      </span>
      {user?.role === 'admin' && (
        <Link to="/admin/automation" className="underline underline-offset-2 hover:text-amber-900">
          Manage
        </Link>
      )}
    </div>
  );
}
